/**
 * Smart Nudges System
 * Detects behavior gaps and suggests small actions
 * Uses gentle, supportive tone and smart frequency limiting
 */

/**
 * Analyze user behavior and generate nudges
 * @param {Object} userData - Current user data (today's activities, history)
 * @param {Object} lastNudges - Last shown nudges with timestamps
 * @returns {Array} - Array of nudge objects
 */
export const generateSmartNudges = (userData, lastNudges = {}) => {
  const nudges = [];
  const now = new Date();
  const currentHour = now.getHours();
  
  // Don't nudge during sleep hours (10pm - 7am)
  if (currentHour >= 22 || currentHour < 7) {
    return [];
  }
  
  // Check each behavior gap
  const hydrationNudge = checkHydration(userData, lastNudges, currentHour);
  if (hydrationNudge && shouldShowNudge(lastNudges, 'hydration', 4)) {
    nudges.push(hydrationNudge);
  }
  
  const activityNudge = checkActivity(userData, lastNudges, currentHour);
  if (activityNudge && shouldShowNudge(lastNudges, 'activity', 6)) {
    nudges.push(activityNudge);
  }
  
  const sleepNudge = checkSleep(userData, lastNudges, currentHour);
  if (sleepNudge && shouldShowNudge(lastNudges, 'sleep', 12)) {
    nudges.push(sleepNudge);
  }
  
  const checkInNudge = checkDailyCheckIn(userData, lastNudges, currentHour);
  if (checkInNudge && shouldShowNudge(lastNudges, 'checkin', 8)) {
    nudges.push(checkInNudge);
  }
  
  const streakNudge = checkStreak(userData, lastNudges);
  if (streakNudge && shouldShowNudge(lastNudges, 'streak', 24)) {
    nudges.push(streakNudge);
  }
  
  // Sort by priority and return top 2 (avoid overwhelming)
  return nudges
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 2);
};

/**
 * Check if enough time has passed since last nudge
 */
const shouldShowNudge = (lastNudges, type, hoursSince) => {
  if (!lastNudges[type]) return true;
  
  const lastShown = new Date(lastNudges[type]);
  const now = new Date();
  const hoursDiff = (now - lastShown) / (1000 * 60 * 60);
  
  return hoursDiff >= hoursSince;
};

/**
 * Check hydration and generate nudge if needed
 */
const checkHydration = (userData, lastNudges, currentHour) => {
  const today = userData.today || {};
  const history = userData.history || [];
  
  // If user hasn't logged water today
  if (!today.water && currentHour >= 10) {
    // Check pattern - do they usually log water?
    const recentWaterDays = history.slice(-3).filter(d => d.water).length;
    
    if (recentWaterDays >= 2) {
      // User usually logs water, remind them
      return {
        type: 'hydration',
        priority: 8,
        title: 'Stay Hydrated',
        message: 'You haven\'t logged water today — a quick glass may help your energy',
        icon: '💧',
        action: {
          text: 'Log Water',
          route: '/daily-loop'
        },
        dismissible: true,
        tone: 'gentle'
      };
    } else if (currentHour >= 14) {
      // It's afternoon and still no water
      return {
        type: 'hydration',
        priority: 9,
        title: 'Hydration Check',
        message: 'It\'s been a while — consider having some water',
        icon: '💧',
        action: {
          text: 'Got it',
          route: '/daily-loop'
        },
        dismissible: true,
        tone: 'gentle'
      };
    }
  }
  
  return null;
};

/**
 * Check activity and generate nudge if needed
 */
const checkActivity = (userData, lastNudges, currentHour) => {
  const today = userData.today || {};
  const history = userData.history || [];
  
  // If user hasn't logged movement and it's afternoon
  if (!today.activity && currentHour >= 15) {
    // Check if they usually move
    const recentActivityDays = history.slice(-5).filter(d => d.activity).length;
    
    if (recentActivityDays >= 3) {
      return {
        type: 'activity',
        priority: 7,
        title: 'Time to Move',
        message: 'You haven\'t moved much today — a short walk could boost your mood',
        icon: '🚶',
        action: {
          text: 'Take a Break',
          route: '/daily-loop'
        },
        dismissible: true,
        tone: 'encouraging'
      };
    }
  }
  
  // If they're on a streak, encourage them
  const streak = userData.movementStreak || 0;
  if (streak >= 3 && !today.activity && currentHour >= 12) {
    return {
      type: 'activity',
      priority: 8,
      title: 'Keep Your Streak',
      message: `You're on a ${streak}-day movement streak! Don't break it now`,
      icon: '🔥',
      action: {
        text: 'Log Movement',
        route: '/daily-loop'
      },
      dismissible: true,
      tone: 'motivating'
    };
  }
  
  return null;
};

/**
 * Check sleep pattern and generate nudge if needed
 */
const checkSleep = (userData, lastNudges, currentHour) => {
  const history = userData.history || [];
  
  // Check recent sleep pattern
  const recentSleepDays = history.slice(-3).filter(d => d.sleep).length;
  
  // If poor sleep recently and it's evening
  if (recentSleepDays <= 1 && currentHour >= 19 && currentHour < 22) {
    return {
      type: 'sleep',
      priority: 9,
      title: 'Rest Earlier Tonight',
      message: 'Your sleep has been low recently — try resting earlier tonight',
      icon: '🌙',
      action: {
        text: 'Set Reminder',
        route: '/my-reminders'
      },
      dismissible: true,
      tone: 'caring'
    };
  }
  
  // If they had good sleep streak but missed yesterday
  const yesterday = history[history.length - 1] || {};
  const beforeYesterday = history[history.length - 2] || {};
  
  if (beforeYesterday.sleep && !yesterday.sleep && currentHour >= 20) {
    return {
      type: 'sleep',
      priority: 7,
      title: 'Sleep Well Tonight',
      message: 'Get back on track with a good night\'s rest',
      icon: '😴',
      action: {
        text: 'Wind Down',
        route: null
      },
      dismissible: true,
      tone: 'supportive'
    };
  }
  
  return null;
};

/**
 * Check daily check-in and generate nudge if needed
 */
const checkDailyCheckIn = (userData, lastNudges, currentHour) => {
  const today = userData.today || {};
  const completedToday = today.water && today.activity && today.sleep !== undefined;
  
  // If they haven't checked in and it's evening
  if (!completedToday && currentHour >= 18 && currentHour < 21) {
    return {
      type: 'checkin',
      priority: 6,
      title: 'Quick Check-In',
      message: 'Take a moment to track how you\'re doing today',
      icon: '📝',
      action: {
        text: 'Check In Now',
        route: '/daily-loop'
      },
      dismissible: true,
      tone: 'gentle'
    };
  }
  
  return null;
};

/**
 * Check streak status and generate motivational nudge
 */
const checkStreak = (userData, lastNudges) => {
  const streak = userData.dailyStreak || 0;
  const today = userData.today || {};
  const completedToday = today.water && today.activity && today.sleep !== undefined;
  
  // If they have a streak and haven't completed today
  if (streak >= 5 && !completedToday) {
    return {
      type: 'streak',
      priority: 10,
      title: 'Protect Your Streak',
      message: `You're on a ${streak}-day streak! Complete today's habits to keep it going`,
      icon: '🔥',
      action: {
        text: 'Complete Now',
        route: '/daily-loop'
      },
      dismissible: true,
      tone: 'motivating'
    };
  }
  
  // Celebrate milestone streaks
  if (streak === 7 || streak === 14 || streak === 30) {
    return {
      type: 'streak',
      priority: 5,
      title: 'Amazing Streak!',
      message: `${streak} days in a row! You're building lasting habits`,
      icon: '🎉',
      action: {
        text: 'View Progress',
        route: '/evolution'
      },
      dismissible: true,
      tone: 'celebrating'
    };
  }
  
  return null;
};

/**
 * Generate mock user data for testing
 */
export const generateMockUserData = () => {
  const history = [];
  
  // Generate 5 days of history
  for (let i = 4; i >= 1; i--) {
    history.push({
      water: Math.random() > 0.3,
      activity: Math.random() > 0.4,
      sleep: Math.random() > 0.3
    });
  }
  
  return {
    today: {
      water: false, // Not logged yet (will trigger nudge)
      activity: false,
      sleep: undefined
    },
    history,
    dailyStreak: 3,
    hydrationStreak: 2,
    movementStreak: 3
  };
};

/**
 * Save nudge as shown (to prevent re-showing)
 * Note: Only stores timestamps (non-sensitive), safe for localStorage
 */
export const markNudgeShown = (type) => {
  const lastNudges = JSON.parse(localStorage.getItem('healthloop_last_nudges') || '{}');
  lastNudges[type] = new Date().toISOString();
  localStorage.setItem('healthloop_last_nudges', JSON.stringify(lastNudges));
};

/**
 * Get last shown nudges
 * Note: Only retrieves timestamps (non-sensitive data)
 */
export const getLastNudges = () => {
  return JSON.parse(localStorage.getItem('healthloop_last_nudges') || '{}');
};

/**
 * Main export function
 */
export const getSmartNudges = (realUserData = null) => {
  const userData = realUserData || generateMockUserData();
  const lastNudges = getLastNudges();
  
  return generateSmartNudges(userData, lastNudges);
};

export default {
  generateSmartNudges,
  getSmartNudges,
  markNudgeShown,
  getLastNudges,
  generateMockUserData
};
